import { collection, doc, getDocs, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase";

export const PARTY_DISCOUNTS_COLLECTION = "party_discounts";

const DOC_ID_SEP = "__";

export function normalizePartyRow(id, raw) {
  const d = raw && typeof raw === "object" ? raw : {};
  return {
    id,
    partyName: String(d.partyName ?? d.partyname ?? d.name ?? "").trim(),
    city: String(d.city ?? "").trim(),
    gst: String(d.gst ?? "").trim(),
    group: String(d.group ?? "").trim(),
    inactive: String(d.inactive ?? "No").trim(),
  };
}

export function normalizeGroupRow(id, raw) {
  const d = raw && typeof raw === "object" ? raw : {};
  return {
    id,
    name: String(d.gname ?? d.name ?? "").trim(),
  };
}

/** Firestore document id for party + group discount row. */
export function partyDiscountDocId(partyId, groupId) {
  const pid = String(partyId || "").trim();
  const gid = String(groupId || "").trim();
  if (!pid) return "";
  if (!gid) return pid;
  return `${pid}${DOC_ID_SEP}${gid}`;
}

function readGroupIdFromDoc(docSnap) {
  const d = docSnap.data() || {};
  const explicit = String(
    d.groupId ?? d.groupid ?? d.brandId ?? d.brandid ?? ""
  ).trim();
  if (explicit) return explicit;
  const id = docSnap.id || "";
  const sep = id.indexOf(DOC_ID_SEP);
  if (sep > 0) return id.slice(sep + DOC_ID_SEP.length);
  return "";
}

function readPartyIdFromDoc(docSnap) {
  const d = docSnap.data() || {};
  const explicit = String(d.partyId ?? "").trim();
  if (explicit) return explicit;
  const id = docSnap.id || "";
  const sep = id.indexOf(DOC_ID_SEP);
  if (sep > 0) return id.slice(0, sep);
  return id;
}

/**
 * @param {{ groupId?: string, partyId?: string }} [opts]
 * @returns {Promise<Map<string, { discountAmount: number, note: string, groupId: string, partyId: string, partyName: string, groupName: string, updatedAt: unknown }>>}
 * Map key = partyId when filtering by groupId; groupId when filtering by partyId; partyId when neither (legacy flat).
 */
export async function loadPartyDiscountAmountMap(opts = {}) {
  const filterGroupId = String(opts.groupId ?? "").trim();
  const filterPartyId = String(opts.partyId ?? "").trim();
  const snap = await getDocs(collection(db, PARTY_DISCOUNTS_COLLECTION));
  const map = new Map();
  snap.forEach((docSnap) => {
    const d = docSnap.data() || {};
    const partyId = readPartyIdFromDoc(docSnap);
    if (!partyId) return;
    const groupId = readGroupIdFromDoc(docSnap);
    if (filterGroupId && groupId !== filterGroupId) return;
    if (filterPartyId && partyId !== filterPartyId) return;
    if (!filterGroupId && !filterPartyId && groupId) return;

    const row = {
      discountAmount: Number(d.discountAmount ?? d.amount ?? 0) || 0,
      note: String(d.note ?? d.remarks ?? "").trim(),
      groupId,
      partyId,
      partyName: String(d.partyName ?? "").trim(),
      groupName: String(d.groupName ?? d.group ?? "").trim(),
      updatedAt: d.updatedAt ?? null,
    };
    const key = filterPartyId ? groupId : partyId;
    if (!key) return;
    map.set(key, row);
  });
  return map;
}

/** All party×group discount rows (amount > 0 or any saved row). */
export async function loadAllPartyDiscountRows() {
  const snap = await getDocs(collection(db, PARTY_DISCOUNTS_COLLECTION));
  const rows = [];
  snap.forEach((docSnap) => {
    const d = docSnap.data() || {};
    const partyId = readPartyIdFromDoc(docSnap);
    const groupId = readGroupIdFromDoc(docSnap);
    if (!partyId || !groupId) return;
    rows.push({
      id: docSnap.id,
      partyId,
      groupId,
      partyName: String(d.partyName ?? "").trim(),
      groupName: String(d.groupName ?? d.group ?? "").trim(),
      discountAmount: Number(d.discountAmount ?? d.amount ?? 0) || 0,
      note: String(d.note ?? d.remarks ?? "").trim(),
      updatedAt: d.updatedAt ?? null,
    });
  });
  return rows;
}

/** Discount rows for one group (parties that have a rate for this group). */
export async function loadDiscountRowsForGroup(groupId) {
  const gid = String(groupId || "").trim();
  if (!gid) return [];
  const map = await loadPartyDiscountAmountMap({ groupId: gid });
  return [...map.entries()].map(([partyId, row]) => ({
    partyId,
    groupId: gid,
    partyName: row.partyName,
    groupName: row.groupName,
    discountAmount: row.discountAmount,
    note: row.note,
    updatedAt: row.updatedAt,
  }));
}

/** Discount rows for one party (groups/brands that party has rates for). */
export async function loadDiscountRowsForParty(partyId) {
  const pid = String(partyId || "").trim();
  if (!pid) return [];
  const map = await loadPartyDiscountAmountMap({ partyId: pid });
  return [...map.entries()].map(([groupId, row]) => ({
    partyId: pid,
    groupId,
    partyName: row.partyName,
    groupName: row.groupName,
    discountAmount: row.discountAmount,
    note: row.note,
    updatedAt: row.updatedAt,
  }));
}

/**
 * @param {{ partyId: string, partyName?: string, groupId: string, groupName?: string, discountAmount: number, note?: string }} row
 */
export async function savePartyDiscountAmount(row) {
  const partyId = String(row.partyId || "").trim();
  const groupId = String(row.groupId || "").trim();
  if (!partyId) throw new Error("Party id is required");
  if (!groupId) throw new Error("Group is required");
  const amount = Number(row.discountAmount);
  if (!Number.isFinite(amount) || amount < 0) {
    throw new Error("Discount amount must be zero or a positive number");
  }
  const docId = partyDiscountDocId(partyId, groupId);
  const groupName = String(row.groupName || row.group || "").trim();
  const payload = {
    partyId,
    partyName: String(row.partyName || "").trim(),
    groupId,
    group: groupName,
    groupName,
    discountAmount: amount,
    amount,
    note: String(row.note || "").trim(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(doc(db, PARTY_DISCOUNTS_COLLECTION, docId), payload, { merge: true });
  return payload;
}
